import { useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Group, Text } from "@mantine/core";
import { useResetSetting, useUpdateSetting } from "../api/admin";
import { ApiError } from "../api/client";
import type { Setting } from "../api/types";
import { Chip } from "../ui/Chip";
import { StatePill } from "../ui/StatePill";
import { FeedbackLine } from "../ui/FeedbackLine";
import { useLayout } from "../ui/useLayout";

// The mono stack the key and read-only value cells are drawn in. Mantine's `ff="monospace"`
// is a different stack, so it is written out here rather than read off the theme.
export const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';

/** Name and key, the control, the actions. */
const ROW: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(200px, 260px) minmax(0, 1fr) auto",
  gap: 12,
  alignItems: "start",
  padding: "8px 0",
  borderTop: "1px solid var(--mantine-color-default-border)",
};

const LABEL_CELL: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 2,
  minWidth: 0,
  overflowWrap: "anywhere",
  paddingTop: 6,
};

export const STATIC_VALUE: CSSProperties = {
  fontFamily: MONO,
  fontSize: 12,
  overflowWrap: "anywhere",
  paddingTop: 8,
};

/**
 * The frame every row draws inside, so all of them carry the same three facts.
 *
 * `hint` sits after the description rather than inside `children` on purpose: the template
 * rows' field list is reference material you read while typing, and it belongs below the
 * sentence that says what the setting is for, not above it.
 */
export function SettingShell({
  setting,
  children,
  actions,
  hint,
  error,
}: {
  setting: Setting;
  children: ReactNode;
  actions?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
}) {
  const { t } = useTranslation("admin");
  const { phone } = useLayout();
  const description = t(`settingsPage.desc.${setting.key}`, { defaultValue: "" });
  return (
    <Box
      style={{ ...ROW, gridTemplateColumns: phone ? "minmax(0, 1fr)" : ROW.gridTemplateColumns }}
    >
      {/* A key the catalogue has no name for falls back to the key itself, which is what
          every unknown-row setting from a newer build gets. */}
      <Box component="label" htmlFor={`setting-${setting.key}`} style={LABEL_CELL}>
        <Text size="sm" lh={1.3}>
          {t(`settingsPage.label.${setting.key}`, { defaultValue: setting.key })}
        </Text>
        <Text size="xs" c="dimmed" style={{ fontFamily: MONO }}>
          {setting.key}
        </Text>
        {setting.scope === "restart" && (
          <StatePill tone="quiet" title={t("settingsPage.restartTitle")}>
            {t("settingsPage.restartRequired")}
          </StatePill>
        )}
      </Box>

      {/* One wrapper so the control and its prose share a single cell of the row grid. */}
      <Box miw={0} style={{ overflowWrap: "anywhere" }}>
        {children}
        {description && (
          <Text size="xs" c="dimmed" mt={4}>
            {description}
          </Text>
        )}
        {hint}
      </Box>

      <Group gap={6} pt={phone ? 0 : 6} wrap="wrap">
        {actions}
      </Group>

      <FeedbackLine role="alert" style={{ gridColumn: "1 / -1" }}>
        {error}
      </FeedbackLine>
    </Box>
  );
}

/** Save one key, holding whatever the server said if it refused. */
export function useSaveSetting(key: string) {
  const update = useUpdateSetting();
  const [error, setError] = useState<string | null>(null);

  const save = async (value: unknown): Promise<boolean> => {
    setError(null);
    try {
      await update.mutateAsync({ key, value });
      return true;
    } catch (caught) {
      // The server type-checks each key. A 422 here is it refusing a value that would have
      // failed silently inside a worker hours later.
      setError(caught instanceof ApiError ? caught.detail : String(caught));
      return false;
    }
  };

  return { save, error };
}

/**
 * Drop the override, on the rows that have one.
 *
 * `onReset` reseeds a local draft from the row the server hands back; a row that holds no
 * draft passes nothing and the invalidation is enough.
 */
export function ResetChip({
  setting,
  onReset,
}: {
  setting: Setting;
  onReset?: (fresh: Setting) => void;
}) {
  const reset = useResetSetting();
  const { t } = useTranslation("admin");
  if (setting.source !== "db") return null;
  return (
    <Chip onClick={() => void reset.mutateAsync(setting.key).then((fresh) => onReset?.(fresh))}>
      {t("settingsPage.reset")}
    </Chip>
  );
}
